import { ExtractedMentions, MentionUser, MentionCell, MentionReference, ParsedMention } from './types';

/**
 * Regular expression to match mentions in markup format
 * Matches: @[Display Name](id) for users, #[Display](id) for references, and [Display](id) for cells
 */
const USER_MENTION_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;
const REFERENCE_MENTION_REGEX = /#\[([^\]]+)\]\(([^)]+)\)/g;
const CELL_MENTION_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Parse markup string to extract all mentions
 * @param markup - Markup string with embedded mentions
 * @returns Array of parsed mentions with positions
 */
export function parseMentions(markup: string): ParsedMention[] {
  const mentions: ParsedMention[] = [];

  // Find all user mentions (@[...])
  USER_MENTION_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = USER_MENTION_REGEX.exec(markup)) !== null) {
    const [fullMatch, display, id] = match;
    const startIndex = match.index;
    const endIndex = startIndex + fullMatch.length;

    mentions.push({
      type: 'user',
      display,
      id,
      startIndex,
      endIndex,
    });
  }

  // Find all reference mentions (#[...])
  REFERENCE_MENTION_REGEX.lastIndex = 0;

  while ((match = REFERENCE_MENTION_REGEX.exec(markup)) !== null) {
    const [fullMatch, display, id] = match;
    const startIndex = match.index;
    const endIndex = startIndex + fullMatch.length;

    mentions.push({
      type: 'reference',
      display,
      id,
      startIndex,
      endIndex,
    });
  }

  // Find all cell mentions ([...]) that are NOT part of user mentions or reference mentions
  CELL_MENTION_REGEX.lastIndex = 0;

  while ((match = CELL_MENTION_REGEX.exec(markup)) !== null) {
    const [fullMatch, display, id] = match;
    const startIndex = match.index;
    const endIndex = startIndex + fullMatch.length;

    // Skip if this is part of a user mention (preceded by @) or reference mention (preceded by #)
    if (startIndex > 0 && (markup[startIndex - 1] === '@' || markup[startIndex - 1] === '#')) {
      continue;
    }

    // Only treat as cell if it matches cell format
    const isCell = /^[A-Z]+[0-9]+/.test(id) || /!/.test(id);

    if (isCell) {
      mentions.push({
        type: 'cell',
        display,
        id,
        startIndex,
        endIndex,
      });
    }
  }

  // Sort by position
  mentions.sort((a, b) => a.startIndex - b.startIndex);

  return mentions;
}

/**
 * Extract user and cell mentions from markup
 * @param markup - Markup string with embedded mentions
 * @returns Extracted mentions organized by type
 */
export function extractMentions(markup: string): ExtractedMentions {
  const parsed = parseMentions(markup);

  const users: MentionUser[] = [];
  const cells: MentionCell[] = [];
  const references: MentionReference[] = [];

  for (const mention of parsed) {
    if (mention.type === 'user') {
      // Check if this user was already added (avoid duplicates)
      if (!users.some((u) => u.id === mention.id)) {
        users.push({
          id: mention.id,
          display: mention.display,
          email: mention.id.includes('@') ? mention.id : undefined,
        });
      }
    } else if (mention.type === 'cell') {
      // Check if this cell was already added (avoid duplicates)
      if (!cells.some((c) => c.id === mention.id)) {
        // Parse sheet name if present (format: Sheet1!A1)
        const parts = mention.id.split('!');
        cells.push({
          id: mention.id,
          display: mention.display,
          sheet: parts.length > 1 ? parts[0] : undefined,
        });
      }
    } else if (mention.type === 'reference') {
      // Check if this reference was already added (avoid duplicates)
      if (!references.some((r) => r.id === mention.id)) {
        // Parse type from id (format: "sheet:SheetName" or "statusItem:123")
        const [typeStr, ...idParts] = mention.id.split(':');
        const actualId = idParts.join(':'); // Rejoin in case id contains ':'
        const refType = typeStr === 'sheet' ? 'sheet' : 'statusItem';

        references.push({
          id: mention.id,
          display: mention.display,
          type: refType,
        });
      }
    }
  }

  return { users, cells, references };
}

/**
 * Convert markup to plain text by removing mention syntax
 * @param markup - Markup string with embedded mentions
 * @returns Plain text with only display names
 */
export function markupToPlainText(markup: string): string {
  // Replace user mentions @[display](id) with just display
  let result = markup.replace(USER_MENTION_REGEX, '$1');
  // Replace reference mentions #[display](id) with just display
  result = result.replace(REFERENCE_MENTION_REGEX, '$1');
  // Replace cell mentions [display](id) with just display
  result = result.replace(CELL_MENTION_REGEX, '$1');
  return result;
}

/**
 * Extract user emails from markup for notifications
 * @param markup - Markup string with embedded mentions
 * @returns Array of unique email addresses
 */
export function extractUserEmails(markup: string): string[] {
  const { users } = extractMentions(markup);
  return users
    .filter((u) => u.email !== undefined)
    .map((u) => u.email as string);
}
